import React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Check, MessageSquare, CheckCircle2, MapPin, Clock } from "lucide-react";
import { formatStringToDate } from "../../utils";

interface MessageRowProps {
  item: {
    id: number;
    jobTitle?: string;
    idealJobTitles?: string;
    lastCommentCreatedAt?: string;
    createdAt: string;
    isResolvedByOwner?: boolean;
    isResolvedByAuthor?: boolean;
    locationDisplayName?: string;
  };
  index: number;
  onView: () => void;
  onResolve: () => void;
  isRole?: boolean;
}

const MessageRow: React.FC<MessageRowProps> = ({
  item,
  index,
  onView,
  onResolve,
  isRole = true,
}) => {
  const isResolved = item.isResolvedByOwner || item.isResolvedByAuthor;
  const title = isRole ? item.jobTitle : item.idealJobTitles;
  const timestamp = item.lastCommentCreatedAt ?? item.createdAt;

  return (
    <div
      className={cn(
        // Base container
        "group relative",
        // Clean card design
        "bg-white dark:bg-slate-800/80",
        // Border styling
        "border border-slate-200 dark:border-slate-700",
        "rounded-xl",
        // Generous spacing for wider layout
        "mb-3 px-6 py-5",
        // Hover effects
        "transition-all duration-200",
        "hover:border-teal-200 dark:hover:border-teal-700",
        "hover:shadow-lg",
        // Reduced motion
        "motion-reduce:transition-none"
      )}
    >
      {/* Wide layout grid */}
      <div className="grid grid-cols-[1fr_auto_auto] gap-6 items-center">
        {/* Left section - Title and metadata */}
        <div className="min-w-0 cursor-pointer" onClick={onView}>
          {/* Status badge row */}
          <div className="flex items-center gap-3 mb-2">
            {!isResolved ? (
              <span className={cn(
                "inline-flex items-center",
                "px-2.5 py-1 rounded-full",
                "text-xs font-semibold uppercase tracking-wide",
                "bg-teal-50 dark:bg-teal-950/50",
                "text-teal-700 dark:text-teal-300",
                "border border-teal-200 dark:border-teal-800"
              )}>
                <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mr-1.5 animate-pulse" />
                Active
              </span>
            ) : (
              <span className={cn(
                "inline-flex items-center",
                "px-2.5 py-1 rounded-full",
                "text-xs font-semibold uppercase tracking-wide",
                "bg-slate-100 dark:bg-slate-700/50",
                "text-slate-500 dark:text-slate-400"
              )}>
                <CheckCircle2 className="w-3 h-3 mr-1.5" />
                Resolved
              </span>
            )}
          </div>

          {/* Title - larger for wider layout */}
          <h3 className={cn(
            "font-serif text-lg font-medium leading-snug",
            "text-slate-800 dark:text-slate-100",
            "group-hover:text-teal-700 dark:group-hover:text-teal-300",
            "transition-colors duration-200",
            "truncate mb-1.5"
          )}>
            {title}
          </h3>

          {/* Metadata row */}
          <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
            {/* Location */}
            {item.locationDisplayName && (
              <span className="flex items-center gap-1.5 truncate">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{item.locationDisplayName}</span>
              </span>
            )}

            {/* Timestamp - visible on mobile too */}
            <span className="flex items-center gap-1.5 flex-shrink-0">
              <Clock className="w-3.5 h-3.5" />
              {formatStringToDate(timestamp)}
            </span>
          </div>
        </div>

        {/* Center - Additional info area (can be used for counts, etc.) */}
        <div className="hidden lg:flex items-center gap-6 px-4 border-l border-slate-100 dark:border-slate-700/50">
          <div className="text-center">
            <div className="text-xs text-slate-400 dark:text-slate-500 mb-0.5">Last Updated</div>
            <time className="text-sm font-medium text-slate-700 dark:text-slate-300 tabular-nums">
              {formatStringToDate(timestamp)}
            </time>
          </div>
        </div>

        {/* Right - Actions with more spacing */}
        <div className="flex items-center gap-3 shrink-0">
          {/* View Button */}
          <Button
            size="default"
            variant="outline"
            onClick={onView}
            className={cn(
              "h-10 px-5",
              "border-slate-200 dark:border-slate-600",
              "text-slate-700 dark:text-slate-300",
              "hover:border-teal-600 hover:text-teal-600",
              "hover:bg-teal-50 dark:hover:bg-teal-950/30",
              "dark:hover:border-teal-500 dark:hover:text-teal-400",
              "transition-all duration-200",
              "font-medium"
            )}
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            View Thread
          </Button>

          {/* Resolve Button */}
          {!isResolved ? (
            <Button
              size="default"
              onClick={onResolve}
              className={cn(
                "h-10 px-5",
                "bg-teal-600 hover:bg-teal-700",
                "dark:bg-teal-600 dark:hover:bg-teal-500",
                "text-white",
                "shadow-sm hover:shadow-md",
                "transition-all duration-200",
                "font-medium"
              )}
            >
              <Check className="h-4 w-4 mr-2" />
              Resolve
            </Button>
          ) : (
            <Button
              size="default"
              variant="secondary"
              disabled
              className={cn(
                "h-10 px-5",
                "bg-slate-100 dark:bg-slate-700/50",
                "text-slate-400 dark:text-slate-500",
                "cursor-not-allowed",
                "font-medium"
              )}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageRow;
